import type { HTMLAttributeAnchorTarget } from 'react';
import type { To } from 'react-router';
import React, { useCallback, useId } from 'react';
import { useNavigate } from 'react-router';

import type { IconName } from '../../assets/Icon/Icon';
import type { BadgeProps, BadgeSize } from '../../components/Badge/Badge';
import type {
  PolymorphicComponentProps,
  PolymorphicRef,
} from '../../utilities/types/polymorphicAsProp';
import type { ControlSize } from '../shared/types';
import { Divider } from '../../assets/Divider/Divider';
import { Icon } from '../../assets/Icon/Icon';
import { devices } from '../../common/devices';
import { Badge } from '../../components/Badge/Badge';
import {
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPopover,
} from '../../components/DropdownMenu/DropdownMenu';
import { IconTooltip } from '../../components/Tooltip/Tooltip';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { isDefined } from '../../utilities/isDefined';
import { HStack } from '../../utilities/Stack/HStack';
import {
  type AccessLevelProp,
  AccessLevel,
  isAccessLevelVisible,
  useAccessLevelContext,
} from '../../utilities/useAccessLevelContext';
import { useViewport } from '../../utilities/useViewport';
import { selectors, transitions } from '../shared/styles';
import {
  controlBorderRadius,
  controlGap,
  controlHeight,
  controlIconSize,
  controlPaddingX,
  controlPaddingY,
  controlVars,
  getControlPreset,
} from '../shared/vars';

const TabIcon = styled(Icon, {
  size: controlIconSize,
  color: '$$tabIconColor',
});
TabIcon.displayName = 'TabIcon';

const TabLabel = styled(Text, {
  color: '$$tabLabelColor',
  fontWeight: '$bold',
});
TabLabel.displayName = 'TabLabel';

const TabContainer = styled('button', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: controlGap,
  heightAll: controlHeight,
  paddingX: controlPaddingX,
  paddingY: controlPaddingY,
  borderRadius: controlBorderRadius,
  whiteSpace: 'nowrap',
  transition: transitions.control,

  [selectors.hover]: {
    backgroundColor: colors.bgNeutralLight,
    $$tabLabelColor: colors.headingNeutralLight,
    $$tabIconColor: colors.bodyNeutralLight,

    [darkThemeSelector]: {
      backgroundColor: colors.bgNeutralDark,
      $$tabLabelColor: colors.headingNeutralDark,
      $$tabIconColor: colors.bodyNeutralDark,
    },
  },

  [selectors.focus]: {
    outline: 'none',
    boxShadow: shadows.focusRingLight,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  variants: {
    hasAnnotation: {
      true: {},
      false: {},
    },
    hasSubtabs: {
      true: {},
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
    disabled: {
      true: {
        cursor: 'not-allowed',
        opacity: 0.5,
      },
      false: {},
    },
    isSelected: {
      true: {
        [selectors.all]: {
          backgroundColor: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,
          $$tabLabelColor: colors.headingBrandLight,
          $$tabIconColor: colors.bodyBrandLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,
            $$tabLabelColor: colors.headingBrandDark,
            $$tabIconColor: colors.bodyBrandDark,
          },
        },
      },
      false: {
        $$tabLabelColor: colors.headingNeutralLight,
        $$tabIconColor: colors.bodyNeutralLight,

        [darkThemeSelector]: {
          $$tabLabelColor: colors.headingNeutralDark,
          $$tabIconColor: colors.bodyNeutralDark,
        },

        [selectors.hover]: {
          $$tabLabelColor: colors.headingNeutralLight,
          $$tabIconColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            $$tabLabelColor: colors.headingNeutralDark,
            $$tabIconColor: colors.bodyNeutralDark,
          },
        },
      },
    },
    ...controlVars,
  },
  compoundVariants: [
    {
      size: 'x-small',
      hasAnnotation: true,
      css: {
        [devices.touch]: {
          paddingRight: '$4',
        },

        [devices.mouse]: {
          paddingRight: '$2',
        },
      },
    },
    {
      size: 'small',
      hasAnnotation: true,
      css: {
        [devices.touch]: {
          paddingRight: '$4',
        },

        [devices.mouse]: {
          paddingRight: '$2',
        },
      },
    },
    {
      size: 'medium',
      hasAnnotation: true,
      css: {
        [devices.touch]: {
          paddingRight: '$4',
        },

        [devices.mouse]: {
          paddingRight: '$2',
        },
      },
    },
    {
      size: 'large',
      hasAnnotation: true,
      css: {
        [devices.touch]: {
          paddingRight: '$8',
        },

        [devices.mouse]: {
          paddingRight: '$6',
        },
      },
    },
    {
      size: 'x-large',
      hasAnnotation: true,
      css: {
        [devices.touch]: {
          paddingRight: '$12',
        },

        [devices.mouse]: {
          paddingRight: '$10',
        },
      },
    },
    {
      hasSubtabs: false,
      isSelected: true,
      css: {},
    },
    {
      internal: true,
      css: {
        $$tabLabelColor: colors.internalBodyLight,
        $$tabIconColor: colors.internalIconLight,

        [selectors.hover]: {
          $$tabLabelColor: colors.internalHeadingLight,
          $$tabIconColor: colors.internalBodyLight,
        },

        [darkThemeSelector]: {
          $$tabLabelColor: colors.internalBodyDark,
          $$tabIconColor: colors.internalIconDark,

          [selectors.hover]: {
            $$tabLabelColor: colors.internalHeadingDark,
            $$tabIconColor: colors.internalBodyDark,
          },
        },
      },
    },
    {
      internal: true,
      isSelected: true,
      css: {
        [selectors.all]: {
          backgroundColor: colors.internalBgLight,
          strokeAll: colors.internalStrokeLight,
          $$tabLabelColor: colors.internalHeadingLight,
          $$tabIconColor: colors.internalBodyLight,

          [darkThemeSelector]: {
            backgroundColor: colors.internalBgDark,
            strokeAll: colors.internalStrokeDark,
            $$tabLabelColor: colors.internalHeadingDark,
            $$tabIconColor: colors.internalBodyDark,
          },
        },
      },
    },
  ],
});
TabContainer.displayName = 'TabContainer';

type TabPropSize = ControlSize;
export interface TabProps {
  /**
   * The level required to access the tab.
   */
  accessLevel?: AccessLevel | null;
  /**
   * Additional annotation such a result count within the tab.
   * */
  annotation?: React.ReactNode | BadgeProps;
  /**
   * Written label for the tab.
   * */
  children?: React.ReactNode;
  /**
   * Set which icon to display, no value displays no icon.
   */
  icon?: IconName;
  /**
   * Boolean to show internal-only styles.
   */
  internal?: boolean;
  /**
   * Key for the tab.
   */
  key?: string;
  /**
   * Written label for the tab.
   */
  label?: React.ReactNode;
  /**
   * Handles the onClick event.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * Marks the currently selected tab.
   */
  selected?: boolean;
  /**
   * Whether the tab is disabled.
   */
  disabled?: boolean;
  /**
   * Size of the tab.
   */
  size?: TabPropSize;
  /**
   * Whether the tab has subtabs.
   */
  subtabs?: TabProps[];
  /**
   * A tooltip to be displayed on hover.
   */
  tooltip?: React.ReactNode;
  /**
   * Routing
   */
  as?: React.ElementType;
  to?: To;
  href?: string;
  target?: HTMLAttributeAnchorTarget;
}

function filterOutNullAccessLevels(tab: TabProps): tab is typeof tab & {
  accessLevel: NonNullable<(typeof tab)['accessLevel']> | undefined;
} {
  return tab.accessLevel !== null;
}

function hasAnnotationContent(annotation: TabProps['annotation']): boolean {
  if (!annotation) return false;
  if (typeof annotation === 'string' || typeof annotation === 'number') {
    return true;
  }
  if (typeof annotation === 'object' && 'label' in annotation) {
    return true;
  }
  return false;
}

function tabsSizeToBadgeSize(size: TabPropSize): BadgeSize {
  if (size === 'x-small') return 'x-small';
  if (size === 'small') return 'small';
  return 'medium';
}

function TabAnnotation({
  accessLevel: accessLevelProp,
  annotation,
  internal,
  size,
  selected,
}: {
  accessLevel?: AccessLevelProp | null;
  annotation: TabProps['annotation'];
  internal: boolean;
  size: TabPropSize;
  selected: boolean;
}) {
  const badgeSize = tabsSizeToBadgeSize(size);
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const isInternal = internal || accessLevel === AccessLevel.Internal;

  if (!annotation) return null;

  if (typeof annotation === 'string' || typeof annotation === 'number') {
    return (
      <Badge internal={isInternal} size={badgeSize} variant={selected ? 'brand' : 'neutral'}>
        {annotation}
      </Badge>
    );
  }
  if (typeof annotation === 'object' && 'label' in annotation) {
    const annotationVariant = annotation.variant || 'neutral';
    return (
      <Badge
        {...annotation}
        arrangement={annotation.arrangement ?? 'leading-icon'}
        internal={isInternal}
        size={badgeSize}
        variant={!annotation.variant && selected ? 'brand' : annotationVariant}
      >
        {annotation.label}
      </Badge>
    );
  }

  return null;
}

function TabInner<Tag extends React.ElementType>(
  {
    accessLevel: accessLevelProp,
    annotation,
    children,
    icon,
    internal,
    label,
    selected = false,
    disabled = false,
    size = 'medium',
    subtabs,
    tooltip,
    onClick,
    as,
    to,
    href,
    target,
    ...remaining
  }: PolymorphicComponentProps<Tag, TabProps>,
  forwardedRef: PolymorphicRef<Tag>,
) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const isInternal = internal || accessLevel === AccessLevel.Internal;
  if (accessLevel === null) return null;

  const hasEnd =
    hasAnnotationContent(annotation) ||
    (accessLevel && isAccessLevelVisible(accessLevel)) ||
    isDefined(tooltip);

  return (
    <TabContainer
      type="button"
      role="tab"
      aria-selected={selected ? 'true' : 'false'}
      ref={forwardedRef}
      isSelected={selected}
      hasAnnotation={hasAnnotationContent(annotation)}
      hasSubtabs={isDefined(subtabs)}
      internal={isInternal}
      size={size}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      as={disabled ? undefined : as}
      to={disabled ? undefined : to}
      href={disabled ? undefined : href}
      target={disabled ? undefined : target}
      {...remaining}
    >
      <AlignStack
        direction="row"
        gap={size === 'x-small' ? 4 : 6}
        preset={getControlPreset(size)}
        start={isDefined(icon) && <TabIcon icon={icon} />}
        end={
          hasEnd && (
            <HStack align="center" spacing={4}>
              {isDefined(annotation) && (
                <TabAnnotation
                  accessLevel={accessLevel}
                  annotation={annotation}
                  internal={isInternal}
                  size={size}
                  selected={selected}
                />
              )}
              {accessLevel && isAccessLevelVisible(accessLevel) && (
                <FeatureBadge type={accessLevel} size="x-small" />
              )}
              {tooltip && (
                <IconTooltip
                  icon="question"
                  size={14}
                  contents={tooltip}
                  variant={selected ? 'brand' : 'neutral'}
                />
              )}
            </HStack>
          )
        }
      >
        <TabLabel>{label || children}</TabLabel>
      </AlignStack>
    </TabContainer>
  );
}

export const Tab = React.forwardRef(TabInner) as <D extends React.ElementType = 'button'>(
  props: PolymorphicComponentProps<D, TabProps> & { ref?: PolymorphicRef<D> },
) => ReturnType<typeof TabInner>;

const TabDivider = styled(Divider, {
  padding: '$4',
});
TabDivider.displayName = 'TabDivider';

const TabsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$4',
  alignItems: 'center',
  height: '100%',

  '@notDesktop': {
    width: '100%',
  },
});
TabsContainer.displayName = 'TabsContainer';

export type TabsProps = {
  /**
   * Pass `Tab` components into this for automatically laid out tabs.
   * */
  children?: React.ReactNode;
  /**
   * Size of the tabs.
   */
  size?: TabPropSize;
  /**
   * Tabs passed in as an array.
   */
  tabs?: TabProps[];
  /**
   * Secondary tabs passed in as an array.
   */
  secondaryTabs?: TabProps[];
  /**
   * If only one tab is visible, don't display it.
   */
  hideSingleTab?: boolean;
};

function TabsArray({ size, tabs, secondaryTabs, hideSingleTab = true }: TabsProps) {
  const { breakpoint } = useViewport();
  const navigate = useNavigate();
  const key = useId();
  const selectedTab =
    tabs?.find((tab) => tab.selected) || secondaryTabs?.find((tab) => tab.selected);
  const selectedSubtab = selectedTab?.subtabs?.find((subtab) => subtab.selected);

  const onClickTab = useCallback(
    (tab: TabProps, e: Event) => {
      if (tab.to) {
        navigate(tab.to);
      }

      tab.onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement>);
    },
    [navigate],
  );

  const filteredTabs = tabs?.filter(filterOutNullAccessLevels);
  const filteredSecondaryTabs = secondaryTabs?.filter(filterOutNullAccessLevels);

  // Don't render needless tabs if there's only one option
  if (hideSingleTab && filteredTabs?.length === 1 && !filteredSecondaryTabs?.length) return null;

  return breakpoint === 'desktop' ? (
    <>
      {filteredTabs?.map((tab) => (
        <React.Fragment key={`${key}-tab-${tab.label}`}>
          <Tab size={size} {...tab} />
          {tab.selected && tab.subtabs?.length && (
            <>
              <Icon icon="chevron-right" size={12} variant="brand" />
              {tab.subtabs?.map((subtab) => (
                <Tab size={size} key={`${key}-subtab-${subtab.label}`} {...subtab} />
              ))}
            </>
          )}
        </React.Fragment>
      ))}
      {!!filteredTabs?.length && !!filteredSecondaryTabs?.length && (
        <TabDivider appearance="thick" direction="column" variant="neutral" />
      )}
      {filteredSecondaryTabs?.map((secondaryTab) => (
        <React.Fragment key={`${key}-secondaryTab-${secondaryTab.label}`}>
          <Tab size={size} {...secondaryTab} />
          {secondaryTab.selected && secondaryTab.subtabs?.length && (
            <>
              <Icon icon="chevron-right" size={12} variant="brand" />
              {secondaryTab.subtabs?.map((subtab) => (
                <Tab size={size} key={`${key}-subtab-${subtab.label}`} {...subtab} />
              ))}
            </>
          )}
        </React.Fragment>
      ))}
    </>
  ) : (
    <DropdownMenu>
      <DropdownMenuButton
        arrangement="leading-icon"
        icon={selectedTab?.icon}
        labelAlign="start"
        labelStretch
        size="medium"
        width="100%"
        annotation={
          typeof selectedTab?.annotation !== 'object' ? selectedTab?.annotation : undefined
        }
      >
        {selectedTab?.label} {selectedSubtab ? ` > ${selectedSubtab.label}` : null}
      </DropdownMenuButton>
      <DropdownMenuPopover align="start" matchTrigger>
        {!!filteredTabs?.length && (
          <DropdownMenuGroup>
            {filteredTabs.map((tab) => {
              const hasSubtabs = tab.subtabs?.length;
              if (hasSubtabs) {
                return (
                  <DropdownMenuGroup key={`${key}-tab-${tab.label}`} label={tab.label} isNested>
                    {tab.subtabs?.map((subtab) => (
                      <DropdownMenuItem
                        key={`${key}-subtab-${subtab.label}`}
                        onSelect={(e) => onClickTab(subtab, e)}
                        accessLevel={subtab.accessLevel ?? undefined}
                        badge={
                          typeof subtab.annotation !== 'object'
                            ? { label: subtab.annotation }
                            : undefined
                        }
                        icon={subtab.icon}
                        label={subtab.label || subtab.children}
                        internal={tab.internal}
                        tooltip={tab.tooltip}
                        selected={selectedSubtab?.label === subtab.label}
                        disabled={subtab.disabled}
                      />
                    ))}
                  </DropdownMenuGroup>
                );
              }

              return (
                <DropdownMenuItem
                  key={`${key}-tab-${tab.label}`}
                  onSelect={(e) => onClickTab(tab, e)}
                  accessLevel={tab.accessLevel}
                  badge={typeof tab.annotation !== 'object' ? { label: tab.annotation } : undefined}
                  icon={tab.icon}
                  label={tab.label || tab.children}
                  internal={tab.internal}
                  tooltip={tab.tooltip}
                  selected={selectedTab?.label === tab.label}
                  disabled={tab.disabled}
                />
              );
            })}
          </DropdownMenuGroup>
        )}
        {!!filteredSecondaryTabs?.length && (
          <DropdownMenuGroup>
            {filteredSecondaryTabs.map((secondaryTab) => (
              <DropdownMenuItem
                key={`${key}-secondaryTab-${secondaryTab.label}`}
                onSelect={(e) => onClickTab(secondaryTab, e)}
                accessLevel={secondaryTab.accessLevel}
                badge={
                  typeof secondaryTab.annotation !== 'object'
                    ? { label: secondaryTab.annotation }
                    : undefined
                }
                icon={secondaryTab.icon}
                label={secondaryTab.label || secondaryTab.children}
                internal={secondaryTab.internal}
                tooltip={secondaryTab.tooltip}
                selected={selectedTab?.label === secondaryTab.label}
                disabled={secondaryTab.disabled}
              />
            ))}
          </DropdownMenuGroup>
        )}
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}

export function Tabs({
  children,
  size,
  tabs,
  secondaryTabs,
  hideSingleTab,
  ...remaining
}: TabsProps) {
  const isArray = Array.isArray(tabs) || Array.isArray(secondaryTabs);
  return (
    <TabsContainer role="tablist" {...remaining}>
      {isArray ? (
        <TabsArray
          size={size}
          tabs={tabs}
          secondaryTabs={secondaryTabs}
          hideSingleTab={hideSingleTab}
        />
      ) : (
        children || tabs || secondaryTabs
      )}
    </TabsContainer>
  );
}
